import React, { useState } from 'react';
import api from '../services/api';

const DocumentUpload = ({ type = 'invoice', onDataExtracted }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/tiff', 'application/pdf'];
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Please upload an image (JPEG, PNG, GIF, WEBP, TIFF) or PDF file');
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('document', file);

      const endpoint = type === 'invoice' ? '/ocr/invoice' : '/ocr/cost';
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        console.log('[DocumentUpload] Backend response received:');
        console.log('Full response:', JSON.stringify(response.data, null, 2));
        console.log('extractedData:', JSON.stringify(response.data.extractedData, null, 2));
        console.log('file:', JSON.stringify(response.data.file, null, 2));

        setExtractedData(response.data);

        // Pass extracted data to parent component
        if (onDataExtracted) {
          onDataExtracted(response.data.extractedData, response.data.file);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to process document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setExtractedData(null);
    setError('');
  };

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-blue-200 dark:border-gray-700 overflow-hidden mb-6">
      <div className="bg-gradient-to-r from-pastel-blue-500 to-pastel-purple-500 px-6 py-4">
        <h3 className="text-xl font-bold text-white">Upload Document for Auto-Fill</h3>
        <p className="text-sm text-white/90 mt-1">
          Upload an invoice, receipt, or document image/PDF, and we'll automatically extract the data for you.
        </p>
      </div>

      <div className="p-6">
        {!file && (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-pastel-purple-400 dark:hover:border-pastel-purple-600 transition-colors">
            <input
              type="file"
              id="document-upload"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <label htmlFor="document-upload" className="cursor-pointer flex flex-col items-center space-y-3">
              <svg className="w-12 h-12 text-pastel-purple-500 dark:text-pastel-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="text-lg font-medium text-gray-700 dark:text-gray-300">Click to upload or drag and drop</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">Supported: JPEG, PNG, GIF, WEBP, TIFF, PDF (max 10MB)</span>
            </label>
          </div>
        )}

        {file && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-pastel-purple-600 dark:text-pastel-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                  <polyline points="13 2 13 9 20 9" />
                </svg>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{file.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{(file.size / 1024).toFixed(2)} KB</div>
                </div>
              </div>
              <button
                onClick={handleClear}
                type="button"
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {preview && (
              <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <img src={preview} alt="Preview" className="w-full h-auto" />
              </div>
            )}

            {!extractedData && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                type="button"
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-pastel-blue-600 to-pastel-purple-600 hover:from-pastel-blue-700 hover:to-pastel-purple-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Extract Data with OCR</span>
                  </>
                )}
              </button>
            )}

            {extractedData && (
              <div className="flex items-center space-x-3 p-4 bg-pastel-green-50 dark:bg-pastel-green-900/20 border border-pastel-green-200 dark:border-pastel-green-800 rounded-lg">
                <svg className="w-6 h-6 text-pastel-green-600 dark:text-pastel-green-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="text-pastel-green-800 dark:text-pastel-green-200">
                  Data extracted successfully! Review the auto-filled fields below.
                </span>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;
